import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChartBar, Brain, TrendingUp, Gauge, AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ScoringMetrics {
  relevance_score: number;
  virality_score: number;
  quality_score: number;
  performance_score: number;
}

interface ContentMetadata {
  relevance_score?: number;
  performance_score?: number;
}

interface QualityMetricsRow {
  readability_score: number;
  grammar_score: number;
  plagiarism_score: number;
}

export const ComprehensiveScoring = ({ contentId }: { contentId?: string }) => {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['comprehensive-metrics', contentId],
    queryFn: async () => {
      if (!contentId) throw new Error("No content ID provided");

      const [contentMetrics, viralityMetrics, qualityMetrics] = await Promise.all([
        supabase
          .from('generated_content')
          .select('metadata')
          .eq('id', contentId)
          .maybeSingle(),
        supabase
          .from('content_virality_metrics')
          .select('virality_score')
          .eq('content_id', contentId)
          .maybeSingle(),
        supabase
          .from('content_quality_metrics')
          .select('readability_score, grammar_score, plagiarism_score')
          .eq('content_id', contentId)
          .maybeSingle()
      ]);

      const metadata = contentMetrics.data?.metadata as ContentMetadata;
      const qualityData = qualityMetrics.data as QualityMetricsRow;
      
      const qualityScore = qualityData ? 
        ((qualityData.readability_score + qualityData.grammar_score + (1 - qualityData.plagiarism_score)) / 3) : 0;

      return {
        relevance_score: metadata?.relevance_score || 0,
        virality_score: viralityMetrics.data?.virality_score || 0,
        quality_score: qualityScore,
        performance_score: metadata?.performance_score || 0
      } as ScoringMetrics;
    },
    enabled: !!contentId
  });

  if (!contentId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No Content Selected</AlertTitle>
        <AlertDescription>
          Please select or generate content to view metrics.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-8 w-full" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error Loading Metrics</AlertTitle>
        <AlertDescription>
          Unable to load scoring metrics. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const calculateOverallScore = (metrics: ScoringMetrics) => {
    return (
      (metrics.relevance_score * 0.3) +
      (metrics.virality_score * 0.3) +
      (metrics.quality_score * 0.2) +
      (metrics.performance_score * 0.2)
    );
  };

  const overallScore = metrics ? calculateOverallScore(metrics) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ChartBar className="h-5 w-5" />
          Comprehensive Content Scoring
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Overall Score</span>
            <span className="font-medium">{(overallScore * 100).toFixed(1)}%</span>
          </div>
          <Progress value={overallScore * 100} className="h-2" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Brain className="h-4 w-4" />
              <span>Relevance</span>
              <span className="ml-auto">{(metrics?.relevance_score * 100).toFixed(1)}%</span>
            </div>
            <Progress value={metrics?.relevance_score * 100} className="h-1" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="h-4 w-4" />
              <span>Virality</span>
              <span className="ml-auto">{(metrics?.virality_score * 100).toFixed(1)}%</span>
            </div>
            <Progress value={metrics?.virality_score * 100} className="h-1" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <ChartBar className="h-4 w-4" />
              <span>Quality</span>
              <span className="ml-auto">{(metrics?.quality_score * 100).toFixed(1)}%</span>
            </div>
            <Progress value={metrics?.quality_score * 100} className="h-1" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Gauge className="h-4 w-4" />
              <span>Performance</span>
              <span className="ml-auto">{(metrics?.performance_score * 100).toFixed(1)}%</span>
            </div>
            <Progress value={metrics?.performance_score * 100} className="h-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};